import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getByDotKey, standardReplacements } from '@iotv/core-services';
import { toTitleCase } from '@iotv/datamodel-core';
import { isDataRate, isNonNaNNumber, isValidAttributeKeyDefinitionOfType, verify } from '@iotv/iotv-v3-types';
import { Button, ButtonGroup, Checkbox, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { AutosizedTextField } from '../../../../../../../components/common/AutosizedTextField';
import { AppMainViewContext } from '../../../../../../../ContextProviders/AppContextProvider';
import { DeviceControlContext } from '../../DeviceControlContext';
import { BinaryControl } from './BinaryControl';
import { standardOutlinedStackProps, standardStackLabelProps } from './cosmetics';
export const NumericControl = ({ state, dispatch, stateKey: key, label, unitLabel, maxWidth = '42ch', reported }) => {
    const { valueSettings } = state;
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { state: DeviceControlContextState, dispatch: DeviceControlDispatch } = useContext(DeviceControlContext);
    const { rangeLimits, resolvedDownlinkAttributes, correspondingUplinkAttributeMap } = DeviceControlContextState;
    const correspondingUplinkKey = correspondingUplinkAttributeMap?.[key] ?? key;
    const rawReportedValue = getByDotKey(correspondingUplinkKey, reported ?? {});
    const attributeValidation = rangeLimits?.[key];
    const [nonNumVal, setNonNumVal] = useState(null);
    const handleNumChange = (e) => {
        const value = e.target.value;
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
            setNonNumVal(null);
            dispatch({ type: 'UpdateValue', update: { key, v: parsedValue } });
        }
        else {
            setNonNumVal(value);
        }
    };
    const handleLimitedNumChange = (e) => {
        const value = e.currentTarget.value;
        console.log(`TEMP: handleLimitedNumChange`, { value, key });
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
            setNonNumVal(null);
            dispatch({ type: 'UpdateValue', update: { key, v: parsedValue } });
        }
        else {
            setNonNumVal(value);
        }
    };
    // For as yet unknown reasons, using the helper fn getValidRangeText() here causes this component to stop executing, so we are inlining the logic for now.
    let validRangeText;
    const { range } = isValidAttributeKeyDefinitionOfType(attributeValidation, 'number') ? attributeValidation : {};
    if (range) {
        const { min, max } = range;
        validRangeText = `(${min} - ${max})`;
    }
    if (!valueSettings[key]) {
        return _jsx(Typography, { children: `Can't find key ${key} (Numeric)` });
    }
    else {
        const { include, value } = valueSettings[key];
        const isBinary = range && range.max === 1 && range.min === 0;
        if (isBinary) {
            return _jsx(BinaryControl, { state, dispatch, stateKey: key, label, maxWidth, reported, unitLabel });
        }
        const isVerify = range && range.max === verify && range.min === verify;
        const verifyView = isVerify ? (_jsxs(Stack, { ...standardOutlinedStackProps, position: 'relative', minWidth: maxWidth, maxWidth, paddingBlock: 1.5, children: [_jsx(Typography, { ...standardStackLabelProps, children: `${label}` }), _jsx(Checkbox, { checked: include,
                    sx: { padding: 0 },
                    onClick: () => {
                        dispatch({ type: 'TogglePresence', update: { key } });
                    } }), _jsx(Typography, { sx: { opacity: 0.6, fontStyle: 'italic' }, children: `(Verify)` })] })) : null;
        const labelModifier = (key, value) => {
            if (key === 'adr') {
                if (value === 0) {
                    return `OFF`;
                }
                else if (value === 1) {
                    return `ADR ON`;
                }
                else if (value === 2) {
                    return `ABE ON`;
                }
                return `ADR ${value}`;
            }
            else if (key === 'dr') {
                if (isDataRate(value)) {
                    return `DR${value} (SF ${12 - value})`;
                }
            }
            else {
                return `${key} ${value}`;
            }
        };
        const limitedRangeView = !isVerify && range && range.max - range.min < 8 ? (_jsxs(Stack, { ...standardOutlinedStackProps,
            maxWidth: maxWidth ? maxWidth : '100%',
            position: 'relative',
            paddingBlock: 1.5, children: [_jsx(Typography, { ...standardStackLabelProps, children: toTitleCase(standardReplacements[key] ?? `${key}`) }), _jsx(Checkbox, { checked: include,
                    sx: { padding: 0 },
                    onClick: () => {
                        dispatch({ type: 'TogglePresence', update: { key } });
                    } }, `${key}-checkbox`), _jsx(ButtonGroup, { sx: { flexWrap: isSmallView ? 'wrap' : 'nowrap' }, children: Array.from({ length: range.max - range.min + 1 }, (_, i) => range.min + i).map((r) => {
                        const modifiedLabel = labelModifier(key, r);
                        return (_jsx(Tooltip, { title: modifiedLabel,
                            arrow: true,
                            placement: 'top',
                            slotProps: { popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }, children: _jsx("span", { children: _jsx(Button, { variant: value === r ? 'contained' : 'outlined',
                                    size: 'small',
                                    onClick: handleLimitedNumChange,
                                    disabled: !include,
                                    value: r,
                                    sx: {
                                        borderStyle: rawReportedValue === r ? 'dashed' : 'reset',
                                        borderWidth: rawReportedValue === r ? 2 : 1,
                                    }, children: modifiedLabel }) }) }, r));
                    }) }), _jsxs(Typography, { children: ["(", _jsx(Typography, { component: "span", sx: { fontStyle: 'italic' }, children: "Reported" }), ": ", isNonNaNNumber(rawReportedValue) ? labelModifier(key, rawReportedValue) : 'N/A', ")"] })] })) : null;
        const fullNumberView = (_jsx(Stack, { direction: 'row', alignItems: 'center', children: _jsx(AutosizedTextField, { label,
                disabled: !include,
                value: nonNumVal ?? value,
                onChange: handleNumChange,
                slotProps: {
                    inputLabel: { shrink: true },
                    input: {
                        type: 'number',
                        sx: { maxWidth }, // TODO width!!
                        startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsxs(Stack, { direction: 'row', sx: { width: '16ch' }, children: [_jsx(Checkbox, { checked: include,
                                        sx: { padding: 0 },
                                        onClick: () => {
                                            dispatch({ type: 'TogglePresence', update: { key } });
                                        } }, `${key}-checkbox`), _jsx(Typography, { children: validRangeText })] }) }, `${key}-start-adornment`)),
                        endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsxs(Stack, { direction: 'row', alignItems: 'flex-end', gap: 1, children: [_jsx(Typography, { children: unitLabel ?? 'Units' }), _jsxs(Typography, { children: ["(", _jsx(Typography, { component: "span", sx: { fontStyle: 'italic' }, children: "Reported" }), ": ", isNonNaNNumber(rawReportedValue) ?? 'N/A', ")"] })] }) }, `${key}-end-adornment`)),
                    },
                } }, key) }));
        return verifyView ?? limitedRangeView ?? fullNumberView;
    }
};
